import type { TableColumnGroupDefinition } from "~types/tableColumnGroupDefinition.ts";
import type { UiConfiguration } from "~types/uiConfiguration.ts";

const fixturesColumnGroups: Array<TableColumnGroupDefinition> = [
  {
    titleIntlKey: "fixtures.table.settings.fixture.details",
    columns: [
      { key: "added" },
      { key: "attendance" },
      { key: "discipline" },
      { key: "endTimeActualUtc" },
      { key: "externalId" },
      { key: "fixtureProfile" },
      { key: "internationalReference" },
      { key: "poolCode" },
      { key: "profileId" },
      { key: "roundCode" },
      { key: "roundNumber", isDefault: true },
      { key: "series", isDefault: true },
      { key: "seriesCode" },
      { key: "seriesFixtureNumber" },
      { key: "stageCode" },
      { key: "timesUnconfirmed" },
      { key: "awayCompetitor", isDefault: true },
      { key: "awayParticipants", isDefault: true },
      { key: "fixtureId" },
      { key: "fixtureNumber", isDefault: true },
      { key: "fixtureType", isDefault: true },
      { key: "homeCompetitor", isDefault: true },
      { key: "homeParticipants", isDefault: true },
      { key: "nameLatin", isDefault: true },
      { key: "nameLocal", isDefault: true },
      { key: "pool", isDefault: true },
      { key: "result", isDefault: true },
      { key: "round" },
      { key: "stage", isDefault: true },
      { key: "startTime", isDefault: true },
      { key: "startDate", isDefault: true },
      { key: "startTimeUtc" },
      { key: "startTimeActualUtc" },
      { key: "status", isDefault: true },
      { key: "venueId", isDefault: true },
    ],
  },
];

const seasonRosterColumnGroups: Array<TableColumnGroupDefinition> = [
  {
    titleIntlKey: "roster.columns.person.title",
    columns: [
      { key: "personId", isDefault: true },
      { key: "nameFullLocal", isDefault: true },
      { key: "person.externalId", isDefault: true },
      { key: "person.nameFullLatin" },
      { key: "person.additionalDetails.height" },
      { key: "person.additionalDetails.weight" },
      { key: "person.additionalDetails.heightImperial" },
      { key: "person.additionalDetails.weightImperial" },
      { key: "person.additionalDetails.representation" },
      { key: "person.additionalDetails.juniorAssociationLeague" },
      { key: "person.additionalDetails.homeTown" },
      { key: "person.additionalDetails.college" },
      { key: "person.additionalDetails.collegeClass" },
      { key: "person.additionalDetails.school" },
      { key: "person.additionalDetails.schoolClass" },
      { key: "dob" },
      { key: "person.gender" },
      { key: "person.nationality" },
      { key: "person.representing" },
      { key: "person.status" },
    ],
  },
  {
    titleIntlKey: "roster.columns.roster.title",
    columns: [
      { key: "bib", isDefault: true },
      { key: "externalId", isDefault: true },
      { key: "position", isDefault: true },
      { key: "status", isDefault: true },
      { key: "isCaptain", isDefault: true },
    ],
  },
];

const fixtureRosterColumnGroups: Array<TableColumnGroupDefinition> = [
  {
    titleIntlKey: "roster.columns.person.title",
    columns: [
      { key: "personId", isDefault: true },
      { key: "nameFullLocal", isDefault: true },
      { key: "person.externalId", isDefault: true },
      { key: "person.nameFullLatin" },
      { key: "person.additionalDetails.height" },
      { key: "person.additionalDetails.weight" },
      { key: "person.additionalDetails.heightImperial" },
      { key: "person.additionalDetails.weightImperial" },
      { key: "person.additionalDetails.representation" },
      { key: "person.additionalDetails.juniorAssociationLeague" },
      { key: "person.additionalDetails.homeTown" },
      { key: "person.additionalDetails.college" },
      { key: "person.additionalDetails.collegeClass" },
      { key: "person.additionalDetails.school" },
      { key: "person.additionalDetails.schoolClass" },
      { key: "dob" },
      { key: "person.gender" },
      { key: "person.nationality" },
      { key: "person.representing" },
      { key: "person.status" },
    ],
  },
  {
    titleIntlKey: "roster.columns.roster.title",
    columns: [
      { key: "bib", isDefault: true },
      { key: "externalId", isDefault: true },
      { key: "position", isDefault: true },
      { key: "starter", isDefault: true },
      { key: "status", isDefault: true },
      { key: "isCaptain", isDefault: true },
    ],
  },
];

const personsColumnGroups: Array<TableColumnGroupDefinition> = [
  {
    titleIntlKey: "person.columns",
    columns: [
      { key: "personId", isDefault: true },
      { key: "nameFullLocal", isDefault: true },
      { key: "externalId", isDefault: true },
      { key: "nameFullLatin" },
      { key: "additionalDetails.height" },
      { key: "additionalDetails.weight" },
      { key: "additionalDetails.heightImperial" },
      { key: "additionalDetails.weightImperial" },
      { key: "additionalDetails.representation" },
      { key: "additionalDetails.juniorAssociationLeague" },
      { key: "additionalDetails.homeTown" },
      { key: "additionalDetails.college" },
      { key: "additionalDetails.collegeClass" },
      { key: "additionalDetails.school" },
      { key: "additionalDetails.schoolClass" },
      { key: "dob", isDefault: true },
      { key: "gender", isDefault: true },
      { key: "nationality", isDefault: true },
      { key: "status", isDefault: true },
    ],
  },
];

const dashboardColumnGroups: Array<TableColumnGroupDefinition> = [
  {
    titleIntlKey: "dashboard.columns",
    columns: [
      { key: "year", isDefault: true },
      { key: "competitionName", isDefault: true },
      { key: "nameLocal", isDefault: true },
      { key: "gender", isDefault: true },
      { key: "ageGroup", isDefault: true },
      { key: "status", isDefault: true },
    ],
  },
];

export const defaultUiConfig: UiConfiguration = {
  pages: {
    seasonRoster: { tableColumnGroups: seasonRosterColumnGroups },
    fixtureRoster: { tableColumnGroups: fixtureRosterColumnGroups },
    fixtures: { tableColumnGroups: fixturesColumnGroups },
    persons: { tableColumnGroups: personsColumnGroups },
    dashboard: { tableColumnGroups: dashboardColumnGroups },
    seasonUpsert: {
      promotionRelegationRules: {
        enabled: false,
      },
      disciplines: {
        enabled: false,
        isNullable: true,
      },
    },
    entityStatistic: {
      enabled: false,
    },
  },
};
